import { useState, useEffect } from 'react'
import { ApiKeyCredentialForm } from '@/components/integrations/ApiKeyCredentialForm'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { knowledgeApiClient } from '@/lib/api/knowledge.client'
import { toast } from 'sonner'
import { ROUTE_REGISTRY } from '@/lib/routes'
import { INTEGRATIONS, getIntegrationSyncConfig } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'

const CLIENT_ID_DEFAULT = INTEGRATIONS.TENANT_TURNER.CLIENT_ID_SENTINEL

export interface TenantTurnerConnectFormProps {
  isEditMode?: boolean
  onConnected: () => void
}

export function TenantTurnerConnectForm({
  isEditMode = false,
  onConnected,
}: TenantTurnerConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [existingCredential, setExistingCredential] = useState<{
    clientSecret: string
  } | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) return

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const tenantTurner = integrations.find(
          i =>
            i.type === INTEGRATIONS.TENANT_TURNER.TYPE ||
            i.name === INTEGRATIONS.TENANT_TURNER.NAME,
        )
        if (tenantTurner?.connected) {
          setExistingCredential({ clientSecret: '' })
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing credentials')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin])

  const handleSubmit = async (data: {
    clientId?: string
    clientSecret: string
    accountId?: string
  }) => {
    const { clientSecret } = data
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    try {
      if (!isEditMode) {
        const syncConfig = getIntegrationSyncConfig(
          INTEGRATIONS.TENANT_TURNER.TYPE,
        )
        if (
          syncConfig?.supportsSync &&
          syncConfig.syncTypes.includes('LISTINGS')
        ) {
          const existingUrlSync =
            isSuperAdmin && currentCompany?.id
              ? await knowledgeApiClient.getAdminListingUrlSyncStatus(
                  currentCompany.id,
                )
              : await knowledgeApiClient.getListingUrlSyncStatus()
          if (existingUrlSync) {
            if (isSuperAdmin && currentCompany?.id) {
              await knowledgeApiClient.removeAdminListingUrlSync(
                currentCompany.id,
              )
            } else {
              await knowledgeApiClient.removeListingUrlSync()
            }
          }
        }
      }

      if (isEditMode) {
        if (isSuperAdmin) {
          await integrationsClient.tenantTurner.updateAdminCredentials(
            currentCompany.id,
            CLIENT_ID_DEFAULT,
            clientSecret,
          )
        } else {
          await integrationsClient.tenantTurner.updateCredentials(
            CLIENT_ID_DEFAULT,
            clientSecret,
          )
        }
        toast.success('Tenant Turner credentials updated successfully!')
      } else {
        if (isSuperAdmin) {
          await integrationsClient.tenantTurner.createAdminCredentials(
            currentCompany.id,
            CLIENT_ID_DEFAULT,
            clientSecret,
          )
        } else {
          await integrationsClient.tenantTurner.createCredentials(
            CLIENT_ID_DEFAULT,
            clientSecret,
          )
        }
        toast.success('Tenant Turner integration connected successfully!', {
          description: (
            <div className="flex flex-col gap-1">
              <p>We are syncing your listings now.</p>
              <a
                href={`${ROUTE_REGISTRY.KNOWLEDGE.path}?tab=listings`}
                className="text-xs underline underline-offset-2 hover:opacity-70 transition-opacity"
              >
                View listings
              </a>
            </div>
          ),
        })
      }
      onConnected()
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'connecting'} Tenant Turner:`,
        error,
      )
      toast.error(
        `Failed to ${isEditMode ? 'update' : 'connect'} Tenant Turner integration`,
      )
      throw error
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <ApiKeyCredentialForm
      onSubmit={handleSubmit}
      clientSecretConfig={{
        label: 'API Key',
        placeholder: 'Enter your Tenant Turner API key',
        description:
          'You can find your API key in your Tenant Turner account settings',
        initialValue: existingCredential?.clientSecret,
      }}
      submitButtonText={isEditMode ? 'Update' : 'Connect'}
      confirmationText={
        isEditMode
          ? 'Update your API key for Tenant Turner'
          : 'Connect your API key for Tenant Turner'
      }
      componentId="vendor-connect-tenant-turner"
    />
  )
}
